import { AlertService } from './../../../shared/alert/alert.service';
import { SiteContactService } from './../../../core/services/site-contact.service';
import { BrandingService } from './../../../core/services/branding.service';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  CheckExistingPatientResult,
  CreatePatientRegistrationRequest,
  GetSiteContactTrialsRequest
} from 'app/core/models/patient.model';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { LogHelper } from 'app/core/helpers/log.helper';
import { StringHelper } from 'app/core/helpers/string-helper';
import { FileAttachment } from 'app/shared/shared.model';
import { DropdownInputComponent } from 'app/shared/dropdown-input/dropdown-input.component';
import { SelectOption } from 'app/core/models/select-option.model';
import { ConfirmActionModalComponent } from "../../../shared/confirm-action-modal/confirm-action-modal.component";
import { LanguageCultureOptions } from "./../../../core/constants/language-culture-options";
import { requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-patient-register',
  templateUrl: './patient-register.component.html',
  styleUrls: ['./patient-register.component.scss']
})
export class PatientRegisterComponent implements OnInit, OnDestroy {
  @ViewChild('confirmRemoveAttachmentModal') confirmRemoveAttachmentModal: ConfirmActionModalComponent;
  @ViewChild('trialSelect') trialSelect: DropdownInputComponent;
  @ViewChild('siteSelect') siteSelect: DropdownInputComponent;
  @ViewChild('languageSelect') languageSelect: DropdownInputComponent;
  @ViewChild('uploadRegistrationFormInput') uploadRegistrationFormInput: ElementRef;
  @ViewChild('uploadBankDetailsInput') uploadBankDetailsInput: ElementRef;
  @ViewChild('uploadMiscImageInput') uploadMiscImageInput: ElementRef;

  trialOptions: SelectOption[] = [];
  siteOptions: Record<string, SelectOption[]> = {};
  languageOptions: SelectOption[] = [];

  processingRequest: boolean = false;
  showFormErrors = false;
  patientRegistrationCode: string;
  siteContactId: string;

  attachment: FileAttachment;
  bankDetailsAttachment: FileAttachment;
  miscAttachments: FileAttachment[] = [];

  subjectIdWarning: string | null = null;
  subjectIdWarningAcknowledged: boolean = false;
  checkingSubjectId: boolean = false;
  private subjectIdCheck$ = new Subject<void>();
  private subjectIdSubscription: Subscription;

  patientRegistrationForm = new FormGroup({
    trialId: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    siteId: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    investigatorName: new FormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
    formFillerEmail: new FormControl('', [requiredNoWhitespaceValidator(), Validators.email, Validators.maxLength(200)]),
    patientFirstName: new FormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
    patientLastName: new FormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
    patientNumber: new FormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(200)]),
    language: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(200)]),
    screeningDate: new FormControl(null, [Validators.maxLength(100)]),
    cardNumber: new FormControl(null, [Validators.maxLength(100)]),
    comments: new FormControl(null, [Validators.maxLength(500)])
  });

  constructor(private readonly activatedRoute: ActivatedRoute, private siteContactService: SiteContactService, private alertService: AlertService, private brandingService: BrandingService) {
    const languageCultures = LanguageCultureOptions.all();
    languageCultures.forEach(culture => {
      this.languageOptions.push({ value: culture.cultureInfoCode.toLowerCase(), text: culture.language + ' (' + culture.cultureInfoCode.toLowerCase() + ')' });
    });
    this.languageOptions.sort((a, b) => a.text.localeCompare(b.text));
  }

  ngOnInit() {
    this.subscribeTrialChanges();
    this.subscribeSubjectIdCheck();

    this.activatedRoute.params.subscribe(params => {
      this.patientRegistrationCode = params.patientRegistrationCode;
      this.siteContactId = params.siteContactId;

      this.getSiteContactTrials();
    })
  }

  ngOnDestroy() {
    this.subjectIdSubscription?.unsubscribe();
  }

  subscribeTrialChanges() {
    this.patientRegistrationForm.controls.trialId.valueChanges.subscribe(_ => {
      this.siteSelect.reset();
      this.patientRegistrationForm.controls.siteId.setValue('');
      this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
    });
  }

  private subscribeSubjectIdCheck() {
    this.subjectIdSubscription = this.subjectIdCheck$.pipe(
      debounceTime(500),
    ).subscribe(() => this.performSubjectIdCheck());

    // Trigger check when relevant fields change
    const fieldsToWatch = ['patientNumber', 'trialId', 'email'] as const;
    fieldsToWatch.forEach(field => {
      this.patientRegistrationForm.controls[field].valueChanges.pipe(
        distinctUntilChanged()
      ).subscribe(() => {
        this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
        this.subjectIdCheck$.next();
      });
    });
  }

  private performSubjectIdCheck() {
    const trialId = this.patientRegistrationForm.controls.trialId.value;
    const patientCode = this.patientRegistrationForm.controls.patientNumber.value?.trim();
    const email = this.patientRegistrationForm.controls.email.value?.trim();

    if (!trialId || !patientCode || !email) {
      this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
      return;
    }

    this.checkingSubjectId = true;
    this.siteContactService.checkExistingPatient(email, trialId, patientCode).subscribe({
      next: (result: CheckExistingPatientResult) => {
        this.checkingSubjectId = false;

        if (result.patientMatchedByEmail && result.patientMatchedByEmail.patientCode?.toLowerCase() === patientCode.toLowerCase()) {
          this.subjectIdWarning = `This subject already exists on this trial. Are you sure you wish to continue?`;
        } else if (result.patientMatchedByCode) {
          this.subjectIdWarning = `This Subject ID is already in use on this trial for a different patient (${result.patientMatchedByCode.email}).`;
        } else if (result.patientMatchedByEmail) {
          this.subjectIdWarning = `This email address is already in use on this trial by a patient with Subject ID ${result.patientMatchedByEmail.patientCode}.`;
        } else {
          this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
        }
      },
      error: () => {
        this.checkingSubjectId = false;
        this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
      }
    });
  }

  getSiteContactTrials() {
    const request: GetSiteContactTrialsRequest = {
      siteContactId: this.siteContactId,
      patientRegistrationCode: this.patientRegistrationCode
    };

    this.siteContactService.getSiteContactTrials(request)
      .subscribe({
        next: response => {
          this.brandingService.set(response.logoUrl ?? null);

          response.trials.forEach(t => {
            this.trialOptions.push(
              {
                value: t.trialId,
                text: t.publicTrialCode
              });

            this.siteOptions[t.trialId] = [];

            t.sites.forEach(s => {
              this.siteOptions[t.trialId].push({
                value: s.siteId,
                text: s.siteText
              });
            });
          });

          this.preselectTrialAndSite();
        },
        error: () => {
          this.brandingService.set(null);
        }
      })
  };

  private preselectTrialAndSite() {
    if (this.trialOptions.length === 1) {
      this.trialSelect.setValue(this.trialOptions[0].value);
      if (this.siteOptions[this.trialOptions[0].value].length === 1) {
        this.siteSelect.options = this.siteOptions[this.trialOptions[0].value];
        this.siteSelect.setValue(this.siteOptions[this.trialOptions[0].value][0].value);
      }
    }
  }

  createPatientRegistration() {
    if (this.patientRegistrationForm.invalid) {
      this.showFormErrors = true;
      return;
    } else {
      this.showFormErrors = false;
    }

    if (!this.attachment) {
      this.processingRequest = false;
      this.showFormErrors = true;
      return;
    }

    this.processingRequest = true;
    let request = this.patientRegistrationForm.value as CreatePatientRegistrationRequest;
    request.siteContactId = this.siteContactId;
    request.patientRegistrationCode = this.patientRegistrationCode;

    // Upload signed registration file first
    this.siteContactService.uploadFile(this.attachment.data, this.siteContactId, this.patientRegistrationCode).subscribe({
      next: result => {
        request.signedRegistrationFilename = result.signedRegistrationFilename;

        // Then upload bank details if exists
        if (this.bankDetailsAttachment) {
          this.siteContactService.uploadFile(this.bankDetailsAttachment.data, this.siteContactId, this.patientRegistrationCode).subscribe({
            next: bankResult => {
              request.bankDetailsFilename = bankResult.signedRegistrationFilename;
              // Finally upload misc attachments and create registration
              this.uploadMiscAttachmentsAndCreateRegistration(request);
            },
            error: error => {
              LogHelper.log(error);
              this.processingRequest = false;
              this.alertService.showWarningAlert("Failed to upload bank details. Please try again or contact support.");
            }
          });
        } else {
          // No bank details, proceed to misc attachments and creation
          this.uploadMiscAttachmentsAndCreateRegistration(request);
        }
      },
      error: error => {
        LogHelper.log(error);
        this.processingRequest = false;
        this.alertService.showWarningAlert("Failed to upload signed registration form. Please try again or contact support.");
      }
    });
  }

  private uploadMiscAttachmentsAndCreateRegistration(request: CreatePatientRegistrationRequest) {
    if (this.miscAttachments.length === 0) {
      // No misc attachments, proceed to create registration
      this.createRegistration(request);
      return;
    }

    const uploadedFilenames: string[] = [];
    let uploadCount = 0;

    this.miscAttachments.forEach(attachment => {
      this.siteContactService.uploadFile(attachment.data, this.siteContactId, this.patientRegistrationCode).subscribe({
        next: result => {
          uploadedFilenames.push(result.signedRegistrationFilename);
          uploadCount++;

          if (uploadCount === this.miscAttachments.length) {
            // All files uploaded, combine filenames with semicolon separator
            request.miscFilenames = uploadedFilenames.join(';');
            this.createRegistration(request);
          }
        },
        error: error => {
          LogHelper.log(error);
          this.processingRequest = false;
          this.alertService.showWarningAlert("Failed to upload one or more miscellaneous files. Please try again or contact support.");
        }
      });
    });
  }

  private createRegistration(request: CreatePatientRegistrationRequest) {
    this.siteContactService.createPatientRegistration(request).subscribe({
      next: () => {
        this.alertService.showSuccessAlert("Patient registration successfully created");
        this.processingRequest = false;
        this.showFormErrors = false;
        this.subjectIdWarning = null;
      this.subjectIdWarningAcknowledged = false;
        this.patientRegistrationForm.reset();
        this.trialSelect.reset()
        this.siteSelect.reset()
        this.languageSelect.reset()
        this.preselectTrialAndSite();
        this.uploadRegistrationFormInput.nativeElement.value = '';
        this.uploadBankDetailsInput.nativeElement.value = '';
        this.uploadMiscImageInput.nativeElement.value = '';
        this.attachment = null;
        this.bankDetailsAttachment = null;
        this.miscAttachments = [];
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert("Failed to create patient registration. Please try again or contact support.");
        this.processingRequest = false;
      }
    });
  }

  onAddRegistrationForm() {
    this.uploadRegistrationFormInput.nativeElement.click();
  }

  onAddBankDetails() {
    this.uploadBankDetailsInput.nativeElement.click();
  }

  onAddMiscAttachment() {
    this.uploadMiscImageInput.nativeElement.click();
  }

  onRemoveRegistrationForm(entityId: string) {
    if (entityId === 'registration') {
      this.confirmRemoveAttachmentModal.showConfirmation('registration', 'Remove Registration Form?', 'Are you sure you want to remove the signed registration form?');
    } else if (entityId === 'bank') {
      this.confirmRemoveAttachmentModal.showConfirmation('bank', 'Remove Bank Details?', 'Are you sure you want to remove the bank details?');
    } else {
      this.confirmRemoveAttachmentModal.showConfirmation(entityId, 'Remove Miscellaneous Document?', 'Are you sure you want to remove this miscellaneous document?');
    }
  }

  onConfirmRemoveAttachment(entityId: string) {
    if (entityId === 'registration') {
      this.attachment = null;
      this.uploadRegistrationFormInput.nativeElement.value = '';
    } else if (entityId === 'bank') {
      this.bankDetailsAttachment = null;
      this.uploadBankDetailsInput.nativeElement.value = '';
    } else {
      // entityId is the index of the file to remove from this.miscAttachments
      const index = parseInt(entityId, 10);
      if (!isNaN(index) && index >= 0 && index < this.miscAttachments.length) {
        this.miscAttachments.splice(index, 1);
      }
    }
  }

  previewBankDetails(files) {
    if (files.length === 0)
      return;

    if (!this.isFileTypeAllowed(files[0])) {
      this.alertService.showWarningAlert("Invalid file type.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      this.bankDetailsAttachment = {
        data: files[0],
        filename: files[0].name,
        fileUrl: reader.result,
        extension: StringHelper.getFileExtension(files[0].name)
      };
    };
  }

  preview(files) {
    if (files.length === 0)
      return;

    if (!this.isFileTypeAllowed(files[0])) {
      this.alertService.showWarningAlert("Invalid file type.");
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      this.attachment = {
        data: files[0],
        filename: files[0].name,
        fileUrl: reader.result,
        extension: StringHelper.getFileExtension(files[0].name)
      };
    };
  }

  previewMiscAttachments(files) {
    if (files.length === 0)
      return;

    // Process all selected files
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!this.isFileTypeAllowed(file)) {
        this.alertService.showWarningAlert(`Invalid file type for ${file.name}.`);
        continue;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.miscAttachments.push({
          data: file,
          filename: file.name,
          fileUrl: reader.result,
          extension: StringHelper.getFileExtension(file.name)
        });
      };
    }

    // Reset the input so the same file can be selected again if needed
    this.uploadMiscImageInput.nativeElement.value = '';
  }

  private isFileTypeAllowed(file: File): boolean {
    const allowedTypes = /(image\/(jpeg|png|gif))|(application\/pdf)/;
    return allowedTypes.test(file.type);
  }
}
